import Vue from 'vue'
import { addNamespace } from './utils'
import BooksApi from '@/apis/books'
import { isEmptyArray, sortBy } from '@/utils/array'
import { getProperty, isEmptyObject } from '@/utils/object'
import { LessonStoreNamespacedTypes } from '@/store/lessons'
import { HomeworkStoreNamespacedTypes } from '@/store/homework'
import { PdfAssignmentsStoreNamespacedTypes } from '@/store/pdf-assignments'
import { ChapterSectionType } from '@/enums'
import i18next from 'i18next'

const booksApi = new BooksApi()

/**
 * @file
 * @author Thomas Grinderslev <thomas@edulab.dk>
 */

/**
 * @typedef {{books: Array, bookDetails: Object<Number, Object>, bookChapters: Object<Number, Object>, bookErrors: Object<Number, String>, bookChapterErrors: Object<Number, String>, bookChaptersStatus: Object<Number, Object>, errors: Array}} BookStoreState
 */

/**
 * The types used in this store
 * @enum {string}
 */
export const BookStoreTypes = {
  getters: {
    CHAPTER_HAS_TEACHER_CONTENT: 'chapterHasTeacherContent',
    GET_ALL: 'getAll',
    GET_BOOK: 'getBook',
    GET_BOOK_ERROR: 'getBookError',
    GET_CHAPTER: 'getChapter',
    GET_CHAPTER_ERROR: 'getChapterError',
    GET_CHAPTER_INDEX: 'getChapterIndex',
    GET_CHAPTER_NAVIGATION_ITEMS: 'getChapterNavigationItems',
    GET_CHAPTER_NAVIGATION_ITEM_INDEX: 'getNavigationItemIndex',
    GET_CHAPTER_STATUS: 'getChapterStatus',
    GET_CHAPTERS: 'getChapters',
    GET_ERRORS: 'getErrors',
    IS_SELF_TRAINING_COMPLETED: 'isSelfTrainingCompleted',
    IS_TRIAL_EXAM_COMPLETED: 'isTrialExamCompleted',
  },
  actions: {
    FETCH_ALL: 'fetchAll',
    FETCH_BOOK: 'fetchBook',
    FETCH_CHAPTER: 'fetchChapter',
    FETCH_CHAPTER_STATUS: 'fetchChapterStatus',
  },
  mutations: {
    ADD_ERROR: 'addError',
    CLEAR_BOOK_ERROR: 'clearBookError',
    CLEAR_CHAPTER_ERROR: 'clearChapterError',
    SET_BOOKS: 'setBooks',
    SET_BOOK: 'setBook',
    SET_BOOK_ERROR: 'setBookError',
    SET_CHAPTER: 'setChapter',
    SET_CHAPTER_ERROR: 'setChapterError',
    SET_CHAPTER_STATUS: 'setChapterStatus',
  },
}

/**
 * A namespaced version of the types used in this store
 * @enum {string}
 */
export const BookStoreNamespacedTypes = addNamespace('books', BookStoreTypes)

/**
 * @returns {BookStoreState}
 */
export function state() {
  return {
    books: [],
    bookDetails: {},
    bookErrors: {},
    bookChapters: {},
    bookChapterErrors: {},
    bookChaptersStatus: {},
    errors: [],
  }
}

export const getters = {
  /**
   * @param {BookStoreState} state
   * @returns {function():Array}
   */
  [BookStoreTypes.getters.GET_ALL]: state => () => {
    return state.books
  },

  /**
   * @param {BookStoreState} state
   * @returns {function(Number):Object}
   */
  [BookStoreTypes.getters.GET_BOOK]: state => id => {
    return state.bookDetails[id] || null
  },

  /**
   * @param {BookStoreState} state
   * @returns {function(Number):String}
   */
  [BookStoreTypes.getters.GET_BOOK_ERROR]: state => id => {
    return state.bookErrors[id] || ''
  },

  /**
   * @param {BookStoreState} state
   * @param {number} bookId
   * @returns {function():Array}
   */
  [BookStoreTypes.getters.GET_CHAPTERS]: state => bookId => {
    const bookDetails = state.bookDetails[bookId]

    if (bookDetails) {
      return bookDetails.chapters || []
    }

    return []
  },

  /**
   * @param {BookStoreState} state
   * @param {number} chapterId
   * @returns {function(Number):Object}
   */
  [BookStoreTypes.getters.GET_CHAPTER]: state => chapterId => {
    return state.bookChapters[chapterId] || null
  },

  /**
   * @param {BookStoreState} state
   * @param {number} chapterId
   * @returns {function(number):string|undefined}
   */
  [BookStoreTypes.getters.GET_CHAPTER_ERROR]: state => chapterId => {
    return state.bookChapterErrors[chapterId]
  },

  /**
   * @param {BookStoreState} state
   * @param {number} bookId
   * @param {number} chapterId
   * @returns {function():Object}
   */
  [BookStoreTypes.getters.GET_CHAPTER_INDEX]: (state, getters) => ({
    bookId,
    chapterId,
  }) => {
    return (
      (getters[BookStoreTypes.getters.GET_CHAPTERS](bookId) || []).findIndex(
        chapter => chapter.id === chapterId
      ) + 1
    )
  },

  /**
   * @param {BookStoreState} state
   * @param {number} chapterId
   * @returns {function():Object}
   */
  [BookStoreTypes.getters.GET_CHAPTER_STATUS]: state => chapterId => {
    return state.bookChaptersStatus[chapterId] || null
  },

  /**
   * @param {BookStoreState} state
   * @param {number} bookId
   * @param {number} chapterId
   * @returns {function():Object}
   */
  [BookStoreTypes.getters.GET_CHAPTER_NAVIGATION_ITEMS]: (state, getters) => ({
    bookId,
    chapterId,
  }) => {
    const chapterData = getters[BookStoreTypes.getters.GET_CHAPTER](chapterId)

    if (!chapterData) {
      return []
    }

    return [
      {
        id: chapterId,
        sectionType: ChapterSectionType.INTRO,
        title: i18next.t('components.chapter-intro.heading', {
          chapterIndex: getters[BookStoreTypes.getters.GET_CHAPTER_INDEX]({
            bookId,
            chapterId,
          }),
          chapterName: chapterData.name,
        }),
      },
      ...getProperty(chapterData, 'lessons', []).map(({ id, title }) => {
        return {
          id,
          title,
          sectionType: ChapterSectionType.LESSON,
        }
      }),
      ...getProperty(chapterData, 'trialExams', []).map(
        ({ chapterTestId: id, name }) => {
          return {
            id,
            title: name,
            sectionType: ChapterSectionType.TRIAL_EXAM,
          }
        }
      ),
    ]
  },

  /**
   * Returns the index of the navigation item for the given section in the given chapter in the given book.
   *
   * @param {BookStoreState} state
   * @param {VuexGetters} getters
   * @return {function({bookId: number, chapterId: number, sectionType: ChapterSectionType, sectionId: number}): number}
   */
  [BookStoreTypes.getters.GET_CHAPTER_NAVIGATION_ITEM_INDEX]: (
    state,
    getters
  ) => ({ bookId, chapterId, sectionType, sectionId }) => {
    return getters[BookStoreTypes.getters.GET_CHAPTER_NAVIGATION_ITEMS]({
      bookId,
      chapterId,
    }).findIndex(
      item =>
        item.sectionType === sectionType &&
        (item.id === sectionId || sectionType === ChapterSectionType.INTRO)
    )
  },

  /**
   * @param {BookStoreState} state
   * @param {VuexGetters} getters
   * @param {number} chapterId
   * @returns {function(number):boolean}
   */
  [BookStoreTypes.getters.CHAPTER_HAS_TEACHER_CONTENT]: (
    state,
    getters
  ) => chapterId => {
    const chapterData = getters[BookStoreTypes.getters.GET_CHAPTER](chapterId)

    return (
      !!chapterData.teacherDescription ||
      !isEmptyObject(chapterData.activities) ||
      !isEmptyObject(chapterData.pdfAssignments) ||
      !isEmptyArray(chapterData.homeworkIds)
    )
  },

  /**
   * @param {BookStoreState} state
   * @returns {function():Array}
   */
  [BookStoreTypes.getters.GET_ERRORS]: state => () => {
    return state.errors
  },

  /**
   * @param {BookStoreState} state
   * @returns {function(Number, Number):Boolean}
   */
  [BookStoreTypes.getters.IS_SELF_TRAINING_COMPLETED]: state => (
    chapterId,
    lessonId
  ) => {
    return (
      getProperty(
        state.bookChaptersStatus,
        `${chapterId}.lessons.${lessonId}.fiveSharpTestTimesAttended`,
        0
      ) > 0
    )
  },

  /**
   * @param {BookStoreState} state
   * @returns {function(Number, Number):Boolean}
   */
  [BookStoreTypes.getters.IS_TRIAL_EXAM_COMPLETED]: state => (
    chapterId,
    trialExamId
  ) => {
    return (
      getProperty(
        state.bookChaptersStatus,
        `${chapterId}.trialExams.${trialExamId}.timesAttended`,
        0
      ) > 0
    )
  },
}

export const actions = {
  /**
   * @param {VuexCommit} commit
   */
  [BookStoreTypes.actions.FETCH_ALL]({ commit }) {
    if (getters[BookStoreTypes.getters.GET_ALL]().length > 0) {
      return
    }

    booksApi
      .getAll()
      .then(({ books }) => {
        const sortProperty = 'sequence'

        commit(
          BookStoreTypes.mutations.SET_BOOKS,
          books.sort((a, b) => {
            return getProperty(a, sortProperty) - getProperty(b, sortProperty)
          })
        )
      })
      .catch(({ message }) => {
        commit(BookStoreTypes.mutations.ADD_ERROR, message)
      })
  },

  /**
   * @param {VuexCommit} commit
   * @param {VuexGetters} getters
   * @param {number} bookId
   */
  [BookStoreTypes.actions.FETCH_BOOK]({ commit, getters }, bookId) {
    if (getters[BookStoreTypes.getters.GET_BOOK](bookId) !== null) {
      return
    }

    commit(BookStoreTypes.mutations.CLEAR_BOOK_ERROR, { bookId })

    booksApi
      .getBook(bookId)
      .then(bookDetails => {
        bookDetails.chapters = sortBy(bookDetails.chapters || [], 'sequence')

        commit(BookStoreTypes.mutations.SET_BOOK, {
          bookId,
          bookDetails,
        })
      })
      .catch(({ message }) => {
        commit(BookStoreTypes.mutations.SET_BOOK_ERROR, {
          bookId,
          error: message,
        })
      })
  },

  /**
   * @param {VuexCommit} commit
   * @param {VuexDispatch} dispatch
   * @param {VuexGetters} getters
   * @param {Vuex} getters
   * @param {number} chapterId
   */
  [BookStoreTypes.actions.FETCH_CHAPTER](
    { commit, dispatch, getters },
    chapterId
  ) {
    if (getters[BookStoreTypes.getters.GET_CHAPTER](chapterId) !== null) {
      return
    }

    commit(BookStoreTypes.mutations.CLEAR_CHAPTER_ERROR, { chapterId })

    booksApi
      .getChapter(chapterId)
      .then(chapter => {
        chapter.lessons = sortBy(chapter.lessons || [], 'sequence')
        chapter.trialExams = sortBy(chapter.trialExams || [], 'sequence')
        chapter.homework = sortBy(chapter.homework || [], 'sequence')

        const homeworkIds = chapter.homework.map(({ homework }) => homework.id)

        const pdfAssignments = Object.values(chapter.pdfAssignments || {}).map(
          ({ pdfAssignment }) => pdfAssignment
        )
        const pdfAssignmentIds = pdfAssignments.map(pdfAssignment =>
          parseInt(pdfAssignment.id)
        )

        commit(BookStoreTypes.mutations.SET_CHAPTER, {
          chapterId,
          chapter: { homeworkIds, pdfAssignmentIds, ...chapter },
        })

        chapter.homework.forEach(({ homework }) => {
          commit(
            HomeworkStoreNamespacedTypes.mutations.SET_HOMEWORK,
            homework,
            { root: true }
          )
          dispatch(
            HomeworkStoreNamespacedTypes.actions.FETCH_HOMEWORK,
            homework.id,
            { root: true }
          )
        })

        pdfAssignments.forEach(pdfAssignment => {
          commit(
            PdfAssignmentsStoreNamespacedTypes.mutations.SET_PDF_ASSIGNMENT,
            pdfAssignment,
            { root: true }
          )
          dispatch(
            PdfAssignmentsStoreNamespacedTypes.actions.FETCH_PDF_ASSIGNMENT,
            pdfAssignment.id,
            { root: true }
          )
        })
      })
      .catch(({ message }) => {
        commit(BookStoreTypes.mutations.SET_CHAPTER_ERROR, {
          chapterId,
          error: message,
        })
      })
  },

  /**
   * @param {VuexCommit} commit
   * @param {VuexGetters} getters
   * @param {number} chapterId
   */
  [BookStoreTypes.actions.FETCH_CHAPTER_STATUS]({ commit }, chapterId) {
    booksApi
      .getChapterStatus(chapterId)
      .then(status => {
        commit(BookStoreTypes.mutations.SET_CHAPTER_STATUS, {
          chapterId,
          status,
        })

        if (status.lessons) {
          Object.keys(status.lessons).forEach(lessonId => {
            commit(
              LessonStoreNamespacedTypes.mutations.SET_UNDERSTOOD_STATUS,
              {
                lessonId,
                understood: status.lessons[lessonId].understood,
              },
              { root: true }
            )
          })
        }
      })
      .catch(({ message }) => {
        commit(BookStoreTypes.mutations.SET_CHAPTER_ERROR, message)
      })
  },
}

export const mutations = {
  /**
   * @param {BookStoreState} state
   * @param {number} bookId
   */
  [BookStoreTypes.mutations.CLEAR_BOOK_ERROR](state, { bookId }) {
    Vue.delete(state.bookErrors, bookId)
  },

  /**
   * @param {BookStoreState} state
   * @param {number} chapterId
   */
  [BookStoreTypes.mutations.CLEAR_CHAPTER_ERROR](state, { chapterId }) {
    Vue.delete(state.bookChapterErrors, chapterId)
  },

  /**
   * @param {BookStoreState} state
   * @param {Array} books
   */
  [BookStoreTypes.mutations.SET_BOOKS](state, books) {
    state.books = books
  },

  /**
   * @param {BookStoreState} state
   * @param {number} bookId
   * @param {Object} bookDetails
   */
  [BookStoreTypes.mutations.SET_BOOK](state, { bookId, bookDetails }) {
    Vue.set(state.bookDetails, bookId, bookDetails)
  },

  /**
   * @param {BookStoreState} state
   * @param {number} bookId
   * @param {String} error
   */
  [BookStoreTypes.mutations.SET_BOOK_ERROR](state, { bookId, error }) {
    Vue.set(state.bookErrors, bookId, error)
  },

  /**
   * @param {BookStoreState} state
   * @param {number} chapterId
   * @param {Object} chapter
   */
  [BookStoreTypes.mutations.SET_CHAPTER](state, { chapterId, chapter }) {
    Vue.set(state.bookChapters, chapterId, chapter)
  },

  /**
   * @param {BookStoreState} state
   * @param {number} chapterId
   * @param {String} error
   */
  [BookStoreTypes.mutations.SET_CHAPTER_ERROR](state, { chapterId, error }) {
    Vue.set(state.bookChapterErrors, chapterId, error)
  },

  /**
   * @param {BookStoreState} state
   * @param {number} chapterId
   * @param {Object} chapter
   */
  [BookStoreTypes.mutations.SET_CHAPTER_STATUS](state, { chapterId, status }) {
    Vue.set(state.bookChaptersStatus, chapterId, status)
  },

  /**
   * @param {BookStoreState} state
   * @param {Object} error
   */
  [BookStoreTypes.mutations.ADD_ERROR](state, error) {
    state.errors.push(error)
  },
}

export default {
  namespaced: true,
  Types: BookStoreTypes,
  NamespacedTypes: BookStoreNamespacedTypes,
  state,
  getters,
  actions,
  mutations,
}
